import { useEffect, useState } from "react";
import { FilterAlt } from "@mui/icons-material";
import { Grid2, Stack, Typography } from "@mui/material";
import { GridPagination, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import axios from "axios";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import { useRuleWizardContext } from "../../../providers/RuleWizardProvider";
import CoAppDataGridImageCell from "../../global/components/datagrid/CoAppDataGridImageCell";
import DataGridCustomNoRowsOverlay from "../../global/components/datagrid/DataGridCustomNoRowsOverlay";
import { CoAppActionButton, CoAppDataGridPaginationContainer, CoAppDataGridToolbar } from "../../global/styled/global.styled";
import RuleReactionSidebar from "../reactions/RuleReactionSidebar";
import { RuleImage, RuleWizardDataGrid, WizardStepContainer } from "../styled/rule-wizard.styled";

export default function WizardStep2() { 
	const authHeader = useAuthHeader();

	const { 
		setReactions, editedId, ruleLocation, 
		setRuleLocation, editedVersionOfRule, setStepDataHasChanged, platformId 
	} = useRuleWizardContext();

	const [ruleLocations, setRuleLocations] = useState([]);
	const [ruleLocationsLoaded, setRuleLocationLoaded] = useState(false);
	const [ruleLocationsPreviouslyLoaded, setRuleLocationsPreviouslyLoaded] = useState(false);

	const [locationLoaded, setLocationLoaded] = useState(false);

	const [location, setLocation] = useState({});
	const [reactionFields, setReactionFields] = useState([]);
	const [reactionFieldsLoaded, setReactionFieldsLoaded] = useState(false);

	const [reactionTypes, setReactionTypes] = useState([]);

	const initRuleLocations = () => {
		if (!ruleLocationsPreviouslyLoaded) {
			setRuleLocationLoaded(false);
			axios.get(apiRoutes.getRuleLocationsForWizard(platformId), {
				headers: authHeader
			})
				.then(res => {
					setRuleLocations(res.data);
					setRuleLocationLoaded(true);
				})
				.catch(err => console.log(err));

			setRuleLocationsPreviouslyLoaded(true);
		}
	};

	const initSelectedLocation = () => {
		setLocationLoaded(false);
		axios.get(apiRoutes.getRuleLocation(ruleLocation.id), {
			headers: authHeader
		})
			.then(res => {
				setLocation(res.data);
				setLocationLoaded(true);
				setReactionTypes(res.data.reactiontypes);
			})
			.catch(err => console.log(err));
	};

	const initReactionFields = () => {
		setReactionFieldsLoaded(false);
		axios.get(apiRoutes.ruleFields, {
			headers: authHeader,
			params: {
				locationId: ruleLocation.id
			}
		})
			.then(res => {
				setReactionFields(res.data);
				setReactionFieldsLoaded(true);
			})
			.catch(err => console.log(err));
	};

	const resetLocation = () => {
		setRuleLocation("");
		setReactions([]);
		setStepDataHasChanged(true);
		setLocationLoaded(false);
		setReactionFieldsLoaded(false);
	};

	const selectLocation = (locationId) => {
		const location = ruleLocations.filter(location => location.id === locationId)[0];
		setRuleLocation(location);
	};

	function CustomToolbar() {
		return (
			<div style={{ width: "100%" }}>
				<CoAppDataGridToolbar container>
					<Grid2 item xxl={0.1} xl={0.1} lg={0.2} sm={0.2} />
					<Grid2 item xxl={1} xl={1} lg={1.2} sm={1.5}>
						<GridToolbarFilterButton />
					</Grid2>
					<Grid2 item xxl={4} xl={4} lg={3.5} sm={3}>
						<GridToolbarQuickFilter sx={{ width: { xxl: "220px", xl: "220px", lg: "200px", sm: "150px" } }} />
					</Grid2>
					<CoAppDataGridPaginationContainer item>
						<GridPagination rowsPerPageOptions={[]} />
					</CoAppDataGridPaginationContainer>
				</CoAppDataGridToolbar>
			</div>
		);
	}

	const locationColumns = [
		{ field: "name", flex: 1, headerName: "Location name" },
		{ field: "description", flex: 5, headerName: "Description" },
		{
			field: "previewUrl", flex: 3, headerName: "Preview",
			renderCell: (params) => (
				<CoAppDataGridImageCell image={params.value} />
			)
		},
	];

	useEffect(() => {
		if (editedId && Object.keys(editedVersionOfRule).length > 0) {
			if (ruleLocation) {
				if (editedVersionOfRule.ruleLocation) {
					if (ruleLocation.id !== editedVersionOfRule.ruleLocation.id) {
						setStepDataHasChanged(true);
					}
				} else {
					setStepDataHasChanged(true);
				}
			}
		}
		if (ruleLocation) {
			initSelectedLocation();
			initReactionFields();
		}
		initRuleLocations();

	}, [ruleLocation, ruleLocationsLoaded]);

	if (locationLoaded && reactionFieldsLoaded) {
		return (
			<WizardStepContainer>
				<Grid2
					alignItems="center"
					container
					justifyContent={"space-evenly"}
					spacing={2}
					sx={{
						paddingRight: "24px",
						width: "calc(100% - 24px)",
					}}
				>
					<Grid2 md={8} sm={12} 
						sx={{
							justifyContent: "flex-start",
							width: {
								sm: "100%",
								md: "50%",
								lg: "600px",
								xl: "720px",
							},
							minWidth: {
								sm: "100%",
								md: "0",
							},
						}}
					>
						<Stack spacing={2}>
							<>
								<Typography variant="h6">Add reactions to: {location.name}</Typography>
								<CoAppActionButton
									onClick={resetLocation}
									sx={{
										width: "100%",
										maxWidth: {
											sm: "100%",
											md: "180px",
											lg: "300px"
										},
									}}
								>
									Change Location
								</CoAppActionButton>
							</>
							<RuleImage src={location.previewUrl} />
						</Stack>
					</Grid2>

					<Grid2 md={4} sm={12}
						sx={{
							minWidth: {
								sm: "100%",
								md: "0",
							},
							marginBottom: {
								sm: "24px",
								md: "0",
							},
						}}
					>
						<RuleReactionSidebar
							reactionFields={reactionFields}
							reactionTypes={reactionTypes}
						/>
					</Grid2>
				</Grid2>
			</WizardStepContainer>
		);
	} else if (ruleLocationsLoaded) {
		return (
			<WizardStepContainer>
				<Typography sx={{ marginTop: "15px" }} variant="h6">Where should your reaction happen?</Typography>
				<RuleWizardDataGrid
					columns={locationColumns}
					onLocationSelection={selectLocation}
					rows={ruleLocations}
					onRowSelectionModelChange={(locationId) => {
						selectLocation(locationId[0]);
					}}
					sx={{ cursor: "pointer" }}
					slots={{
						toolbar: CustomToolbar,
						noResultsOverlay: DataGridCustomNoRowsOverlay,
						noRowsOverlay: DataGridCustomNoRowsOverlay,
						openFilterButtonIcon: FilterAlt
					}}
					slotProps={{
						noResultsOverlay: { message: "No locations found." },
						noRowsOverlay: { message: "No locations found." }
					}}
					hideFooter
					disableColumnMenu
				/>
			</WizardStepContainer>
		);
	}
}